/**
 * Klick in ein Suchfeld markiert die vorhandene Eingabe, damit sie direkt
 * überschrieben werden kann. Ein zweiter Klick im schon fokussierten Feld
 * setzt den Cursor normal; eine aufgezogene Markierung bleibt stehen.
 */

const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "tel", "url"]);

/** „suchen“, „durchsuchen“, „Suche“, „filtern“, „Filter“. */
const SEARCH_TEXT_RE = /\b(?:durch)?such|\bfiltern?\b/i;

export type SearchFieldSignals = {
  type: string;
  inputMode: string;
  enterKeyHint: string;
  role: string | null;
  placeholder: string;
  ariaLabel: string | null;
  insideSearchLandmark: boolean;
};

export function textMarksSearchField(text: string): boolean {
  return SEARCH_TEXT_RE.test(text);
}

export function matchesSearchFieldSignals(signals: SearchFieldSignals): boolean {
  const type = (signals.type || "text").toLowerCase();
  if (!TEXT_INPUT_TYPES.has(type)) return false;
  if (type === "search") return true;
  if (signals.inputMode.toLowerCase() === "search") return true;
  if (signals.enterKeyHint.toLowerCase() === "search") return true;
  if (signals.role === "searchbox") return true;
  if (signals.insideSearchLandmark) return true;
  return textMarksSearchField(`${signals.placeholder} ${signals.ariaLabel ?? ""}`);
}

export function isSearchFieldInput(target: EventTarget | null): target is HTMLInputElement {
  if (!(target instanceof HTMLInputElement)) return false;
  if (target.disabled || target.readOnly) return false;
  return matchesSearchFieldSignals({
    type: target.type || "text",
    inputMode: target.inputMode || "",
    enterKeyHint: target.enterKeyHint || "",
    role: target.getAttribute("role"),
    placeholder: target.placeholder || "",
    ariaLabel: target.getAttribute("aria-label"),
    insideSearchLandmark: Boolean(target.closest("[role='search']")),
  });
}

function pointerFocusTarget(target: EventTarget | null): EventTarget | null {
  if (target instanceof HTMLLabelElement && target.control) return target.control;
  return target;
}

const installedDocs = new WeakSet<Document>();

/**
 * Hängt die Klick-Markierung an `doc`. Mehrfaches Installieren auf dasselbe
 * Dokument ist wirkungslos; der Rückgabewert entfernt die Listener.
 */
export function installSelectSearchFieldOnFocus(doc: Document = document): () => void {
  if (installedDocs.has(doc)) return () => {};
  installedDocs.add(doc);

  let pointerIntent: EventTarget | null = null;
  let armed: HTMLInputElement | null = null;
  let clearTimer = 0;
  let downX = 0;
  let downY = 0;

  const rememberIntent = (target: EventTarget | null, button: number, x: number, y: number) => {
    if (button !== 0) {
      pointerIntent = null;
      return;
    }
    pointerIntent = pointerFocusTarget(target);
    downX = x;
    downY = y;
  };

  const onPointerDown = (event: PointerEvent) => {
    rememberIntent(event.target, event.button, event.clientX, event.clientY);
  };

  const onMouseDown = (event: MouseEvent) => {
    rememberIntent(event.target, event.button, event.clientX, event.clientY);
  };

  const scheduleClearIntent = () => {
    window.clearTimeout(clearTimer);
    const intent = pointerIntent;
    clearTimer = window.setTimeout(() => {
      if (pointerIntent === intent) pointerIntent = null;
    }, 0);
  };

  const selectAll = (input: HTMLInputElement, valueAtFocus: string) => {
    if (input.ownerDocument.activeElement !== input) return;
    if (input.value !== valueAtFocus || !input.value) return;
    try {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      // Aufgezogene Markierung (Ziehen) nicht überschreiben.
      if (start != null && end != null && start !== end && !(start === 0 && end === input.value.length)) return;
    } catch {
      // selectionStart ist für einzelne Typen nicht lesbar; dann trotzdem markieren.
    }
    input.select();
  };

  const onFocusIn = (event: FocusEvent) => {
    const input = event.target;
    if (pointerIntent !== input || !isSearchFieldInput(input) || !input.value) return;
    const valueAtFocus = input.value;
    armed = input;
    // mouseup setzt danach den Cursor. Auswahl erneut setzen, sobald der Klick vorbei ist.
    window.setTimeout(() => selectAll(input, valueAtFocus), 0);
  };

  const onMouseUp = (event: MouseEvent) => {
    const input = armed;
    armed = null;
    if (input) {
      const moved = Math.hypot(event.clientX - downX, event.clientY - downY) > 4;
      const releasedOnField = pointerFocusTarget(event.target) === input;
      if (!moved && releasedOnField) {
        // Verhindert, dass der Klick die Markierung sofort wieder aufhebt.
        event.preventDefault();
        selectAll(input, input.value);
      }
    }
    scheduleClearIntent();
  };

  doc.addEventListener("pointerdown", onPointerDown, true);
  doc.addEventListener("mousedown", onMouseDown, true);
  doc.addEventListener("pointerup", scheduleClearIntent, true);
  doc.addEventListener("mouseup", onMouseUp, true);
  doc.addEventListener("focusin", onFocusIn);

  return () => {
    window.clearTimeout(clearTimer);
    doc.removeEventListener("pointerdown", onPointerDown, true);
    doc.removeEventListener("mousedown", onMouseDown, true);
    doc.removeEventListener("pointerup", scheduleClearIntent, true);
    doc.removeEventListener("mouseup", onMouseUp, true);
    doc.removeEventListener("focusin", onFocusIn);
    installedDocs.delete(doc);
  };
}
